import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PatientContext = createContext(null);
const LS_KEY = "evercara_active_patient";

export function PatientProvider({ children }) {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [activeId, setActiveIdState] = useState(localStorage.getItem(LS_KEY) || "");
  const [loading, setLoading] = useState(true);

  const setActiveId = (id) => {
    setActiveIdState(id);
    if (id) localStorage.setItem(LS_KEY, id);
    else localStorage.removeItem(LS_KEY);
  };

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const { data } = await api.get("/patients");
      setPatients(data);
      if (data.length && !data.find((p) => p.id === activeId)) {
        setActiveId(data[0].id);
      } else if (!data.length) {
        setActiveId("");
      }
    } finally {
      setLoading(false);
    }
  }, [user, activeId]);

  useEffect(() => {
    if (user && user !== false) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const activePatient = patients.find((p) => p.id === activeId) || null;
  const canEdit = activePatient ? activePatient.role !== "viewer" : false;

  const createPatient = async (payload) => {
    const { data } = await api.post("/patients", payload);
    setPatients((p) => [...p, data]);
    setActiveId(data.id);
    return data;
  };
  const updatePatient = async (id, payload) => {
    const { data } = await api.patch(`/patients/${id}`, payload);
    setPatients((p) => p.map((x) => (x.id === id ? data : x)));
    return data;
  };
  const deletePatient = async (id) => {
    await api.delete(`/patients/${id}`);
    setPatients((p) => {
      const rest = p.filter((x) => x.id !== id);
      if (activeId === id) setActiveId(rest[0]?.id || "");
      return rest;
    });
  };

  return (
    <PatientContext.Provider
      value={{ patients, activePatient, activeId, setActiveId, loading, reload: load, canEdit, createPatient, updatePatient, deletePatient }}
    >
      {children}
    </PatientContext.Provider>
  );
}

export const usePatient = () => useContext(PatientContext);
